/* eslint-disable no-throw-literal */
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { SyntheticEvent, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  CheckCircleFill,
  LockFill,
  ShieldCheck,
  PersonLinesFill,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import ReactGA from "../../../../reactGA";
import { RoutePaths } from "../../../../routes/routes";
import { useAppDispatch, useAppSelector } from "../../../../store/redux-hooks";
import {
  useAddCreditCardMutation,
  useLazyGetCompanyQuery,
  useLazyGetSubscriptionQuery,
  useStartSubscriptionMutation,
} from "../../../../store/slices/apiSlice";
import {
  changeStartSubscriptionCardForm,
  updateStartSubscriptionCardFormErrors,
} from "../../../../store/slices/companyBillingSlice";
import {
  StripePaymentCardDeclineCode,
  StripePaymentCardDeclineCodeMessage,
  STRIPE_SUBSCRIPTION_STATUS,
} from "../../../../util/constants/constants";

export default () => {
  const stripe = useStripe();
  const elements = useElements();

  const startSubscription = useAppSelector(
    (state) => state.companyBilling.startSubscription
  );
  const loadedCompany = useAppSelector((state) => state.company.loadedCompany);
  const dispatch = useAppDispatch();

  const [isProcessing, setIsProcessing] = useState(false);

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [startSubscriptionMutation] = useStartSubscriptionMutation();

  const [addCreditCardMutation] = useAddCreditCardMutation();

  const [getSubscriptionData] = useLazyGetSubscriptionQuery();

  const [getCompany, { data: company, isFetching }] = useLazyGetCompanyQuery();

  useEffect(() => {
    if (loadedCompany?.id) {
      getCompany({ urlArgs: { companyId: loadedCompany?.id } });
    }
  }, [loadedCompany?.id, getCompany]);

  // Add checkout-active class to body for full-page layout (browser compatibility)
  useEffect(() => {
    document.body.classList.add("checkout-active");
    return () => {
      document.body.classList.remove("checkout-active");
    };
  }, []);

  const getOrStartSubscription = async ({
    paymentMethodId,
  }: {
    paymentMethodId: string;
  }) => {
    let result;

    try {
      if (loadedCompany?.id) {
        // attempt to start subscription
        result = await startSubscriptionMutation({
          urlArgs: { companyId: loadedCompany?.id },
          body: {
            priceId: process.env.REACT_APP_STRIPE_PRICE_ID || "",
            vatRateId: process.env.REACT_APP_STRIPE_VAT_RATE_ID || "",
            paymentMethodId: paymentMethodId,
          },
        }).unwrap();
      }
    } catch (e) {
      if (loadedCompany?.id) {
        // sub may already exist if start failed, attempt to fetch it
        result = await getSubscriptionData({
          urlArgs: {
            companyId: loadedCompany.id,
            priceId: process.env.REACT_APP_STRIPE_PRICE_ID || "",
          },
        }).unwrap();
      }
    }

    return result;
  };

  const handleDeclineCodes = (detailedCode: string | null | undefined) => {
    switch (detailedCode) {
      case StripePaymentCardDeclineCode.AUTHENTICATION_REQUIRED:
        throw new Error(
          StripePaymentCardDeclineCodeMessage.AUTHENTICATION_REQUIRED
        );
      //
      case StripePaymentCardDeclineCode.APPROVE_WITH_ID:
        throw new Error(StripePaymentCardDeclineCodeMessage.APPROVE_WITH_ID);
      //
      case StripePaymentCardDeclineCode.CALL_ISSUER:
      case StripePaymentCardDeclineCode.ISSUER_NOT_AVAILABLE:
      case StripePaymentCardDeclineCode.GENERIC_DECLINE:
      case StripePaymentCardDeclineCode.NO_ACTION_TAKEN:
      case StripePaymentCardDeclineCode.NOT_PERMITTED:
      case StripePaymentCardDeclineCode.REVOCATION_OF_ALL_AUTHORIZATIONS:
      case StripePaymentCardDeclineCode.REVOCATION_OF_AUTHORIZATION:
      case StripePaymentCardDeclineCode.SECURITY_VIOLATION:
      case StripePaymentCardDeclineCode.SERVICE_NOT_ALLOWED:
      case StripePaymentCardDeclineCode.STOP_PAYMENT_ORDER:
      case StripePaymentCardDeclineCode.TRANSACTION_NOT_ALLOWED:
      // fallsthrough
      case StripePaymentCardDeclineCode.FRAUDULENT:
      case StripePaymentCardDeclineCode.LOST_CARD:
      case StripePaymentCardDeclineCode.PICKUP_CARD:
      case StripePaymentCardDeclineCode.STOLEN_CARD:
      case StripePaymentCardDeclineCode.RESTRICTED_CARD:
        throw new Error(StripePaymentCardDeclineCodeMessage.CARD_DECLINED);
      //
      case StripePaymentCardDeclineCode.CARD_NOT_SUPPORTED:
        throw new Error(StripePaymentCardDeclineCodeMessage.CARD_NOT_SUPPORTED);
      //
      case StripePaymentCardDeclineCode.CARD_VELOCITY_EXCEEDED:
      case StripePaymentCardDeclineCode.INSUFFICIENT_FUNDS:
      case StripePaymentCardDeclineCode.WITHDRAWAL_COUNT_LIMIT_EXCEEDED:
        throw new Error(StripePaymentCardDeclineCodeMessage.INSUFFICIENT_FUNDS);
      //
      case StripePaymentCardDeclineCode.CURRENCY_NOT_SUPPORTED:
        throw new Error(
          StripePaymentCardDeclineCodeMessage.CURRENCY_NOT_SUPPORTED
        );
      //
      case StripePaymentCardDeclineCode.DUPLICATE_TRANSACTION:
        throw new Error(
          StripePaymentCardDeclineCodeMessage.DUPLICATE_TRANSACTION
        );
      //
      case StripePaymentCardDeclineCode.EXPIRED_CARD:
        throw new Error(StripePaymentCardDeclineCodeMessage.EXPIRED_CARD);
      //
      case StripePaymentCardDeclineCode.PROCESSING_ERROR:
      case StripePaymentCardDeclineCode.REENTER_TRANSACTION:
      case StripePaymentCardDeclineCode.TRY_AGAIN_LATER:
        throw new Error(
          StripePaymentCardDeclineCodeMessage.PAYMENT_UNSUCCESSFUL
        );
      //
      case StripePaymentCardDeclineCode.INCORRECT_NUMBER:
      case StripePaymentCardDeclineCode.INCORRECT_CVC:
      case StripePaymentCardDeclineCode.INCORRECT_ZIP:
      case StripePaymentCardDeclineCode.INCORRECT_PIN:
      case StripePaymentCardDeclineCode.INVALID_EXPIRY_MONTH:
      case StripePaymentCardDeclineCode.INVALID_EXPIRY_YEAR:
      case StripePaymentCardDeclineCode.INVALID_NUMBER:
      case StripePaymentCardDeclineCode.INVALID_ACCOUNT:
      case StripePaymentCardDeclineCode.NEW_ACCOUNT_INFORMATION_AVAILABLE:
        throw new Error(
          StripePaymentCardDeclineCodeMessage.INCORRECT_CARD_DATA
        );

      default:
    }
  };
  const handleSubmit = async (e: SyntheticEvent) => {
    try {
      e.preventDefault();

      setIsProcessing(true);
      setErrorMessage(null);

      if (!loadedCompany?.id) {
        throw new Error(
          "Loading company data, please try again in few seconds..."
        );
      }

      // validate name
      if (!startSubscription.cardholderName) {
        dispatch(
          updateStartSubscriptionCardFormErrors({
            cardholderName: "Please provide cardholder name",
          })
        );
        return;
      }

      if (!stripe || !elements) {
        throw new Error("Loading Stripe, please try again in few seconds...");
      }

      // Get a reference to a mounted CardElement. Elements knows how
      // to find your CardElement because there can only ever be one of
      // each type of element.
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error("Loading Stripe, please try again in few seconds...");
      }

      const newPaymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: startSubscription.cardholderName },
      });

      if (!newPaymentMethod.paymentMethod?.id) {
        throw new Error("Unable to add credit card as payment method!");
      }

      const attachedCreditCard = await addCreditCardMutation({
        urlArgs: { companyId: loadedCompany.id },
        body: { paymentMethodId: newPaymentMethod.paymentMethod?.id },
      }).unwrap();
      let cardAddedSuccessfully = attachedCreditCard?.succedeed;

      if (attachedCreditCard.setupIntent?.status === "requires_action") {
        if ("client_secret" in attachedCreditCard.setupIntent) {
          const { error } = await stripe.confirmCardSetup(
            attachedCreditCard.setupIntent?.["client_secret"]
          );

          if (error) {
            throw new Error(error.message);
          } else {
            cardAddedSuccessfully = true;
          }
        }
      }

      if (!cardAddedSuccessfully) {
        if (
          attachedCreditCard.setupIntent &&
          ("decline_code" in attachedCreditCard.setupIntent ||
            "code" in attachedCreditCard.setupIntent)
        ) {
          // choose decline_code if it's available (since it's more detailed), code otherwise
          const detailedCode =
            attachedCreditCard.setupIntent?.decline_code ||
            attachedCreditCard.setupIntent?.code;
          handleDeclineCodes(detailedCode);
        }

        throw new Error("Unable to add credit card as payment method!");
      }

      const result = await getOrStartSubscription({
        paymentMethodId: newPaymentMethod.paymentMethod.id,
      });
      if (result?.message === "Your card was declined.") {
        const detailedCode =
          result.response?.decline_code || result.response?.code;
        if (detailedCode) {
          handleDeclineCodes(detailedCode);
        }
      }
      if (!result) {
        throw new Error(
          "Unable to start subscription at this time. Please try again later."
        );
      }

      if (
        [
          STRIPE_SUBSCRIPTION_STATUS.ACTIVE,
          STRIPE_SUBSCRIPTION_STATUS.TRIALING,
        ].includes(result?.status)
      ) {
        // Only send GA event for first-time subscribers (those who haven't used their initial coupon yet)
        if (!loadedCompany?.initialCouponUsed) {
          ReactGA.event({
            action: "form_submission",
            label: "hub-subscription-start-success",
            category: "hub-subscription-start",
            value: result.plan?.amount / 100,
          });
        }

        navigate(RoutePaths.BILLING);
      } else {
        throw new Error(
          "Something went wrong activating your subscription. Please try again later."
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e?.message) {
        setErrorMessage(e?.message);
      } else {
        setErrorMessage("Something went wrong, please try again later.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const initialCouponUsed = company?.initialCouponUsed ?? false;

  return (
    <div className="checkout-page">
      {/* Header */}
      <div className="checkout-header">
        <Container>
          <h1 className="checkout-title">Secure Checkout</h1>
          <p className="checkout-subtitle">
            Complete your activation to lock in your{" "}
            {initialCouponUsed ? "subscription" : "90% discount"}.
          </p>
        </Container>
      </div>

      {/* Main Content */}
      <div className="checkout-main">
        <Container>
          <Form onSubmit={handleSubmit}>
            <div className="checkout-content">
              {/* Invoice Summary Card */}
              <div className="checkout-card">
                <h2 className="checkout-card-title">Invoice Summary</h2>

                {isFetching ? (
                  <Row className="justify-content-center my-4">
                    <Col xs="auto">
                      <Spinner
                        as="span"
                        animation="border"
                        role="status"
                        aria-hidden="true"
                      />
                    </Col>
                  </Row>
                ) : (
                  <>
                    <div className="invoice-rows">
                      <div className="invoice-row">
                        <span className="invoice-label">Plan</span>
                        <span className="invoice-value">
                          Bullet All-In-One Toolkit
                        </span>
                      </div>

                      <div className="invoice-row">
                        <div className="invoice-label">Subscription</div>
                        <div className="invoice-value-group">
                          <span className="invoice-value">Monthly License</span>
                          <span className="invoice-subvalue">
                            (Includes Invoicing, Tax, &amp; Payroll)
                          </span>
                        </div>
                      </div>

                      <div className="invoice-row">
                        <span className="invoice-label">Discount</span>
                        <span className="invoice-value">
                          {initialCouponUsed
                            ? "N/A"
                            : "90% Off (First 3 Months)"}
                        </span>
                      </div>
                    </div>

                    <div className="invoice-total-section">
                      <div className="invoice-total-row">
                        <span className="invoice-total-label">
                          Total due today:
                        </span>
                        <span className="invoice-total-value">
                          {initialCouponUsed ? "€49.99" : "€4.99"}
                        </span>
                      </div>
                      <p className="invoice-disclaimer">
                        {initialCouponUsed
                          ? "Billing renews at €49.99/mo. Cancel anytime."
                          : "Once 3 month offer is complete billing renews at standard rate (€49.99/mo). Cancel anytime."}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Payment Card */}
              <div className="checkout-card">
                <div className="payment-header">
                  <LockFill size={14} className="payment-lock-icon" />
                  <h2 className="checkout-card-title mb-0">Pay with Card</h2>
                </div>
                <p className="payment-security-text">
                  Your payment is processed securely. 256-bit SSL Encrypted.
                </p>

                <div className="payment-form">
                  <Form.Group className="mb-3">
                    <Form.Label>Name on card</Form.Label>
                    <Form.Control
                      placeholder="John Smith"
                      type="text"
                      value={startSubscription.cardholderName}
                      onChange={({ target: { value } }) => {
                        dispatch(
                          changeStartSubscriptionCardForm({
                            cardholderName: value,
                          })
                        );
                      }}
                      isInvalid={Boolean(startSubscription.errors.cardholderName)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {startSubscription.errors.cardholderName}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Card number</Form.Label>
                    <CardElement />
                  </Form.Group>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isProcessing || isFetching}
                className="checkout-submit-btn"
              >
                {isProcessing ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  "Confirm & Activate Subscription"
                )}
              </Button>

              {/* Error Message */}
              {errorMessage && (
                <Alert variant="danger" className="mt-3">
                  {errorMessage}
                </Alert>
              )}

              {/* No Lock-in Message */}
              <div className="no-lockin-message">
                <CheckCircleFill size={16} />
                <span>
                  <strong>No Lock-in:</strong> Cancel instantly via your
                  dashboard.
                </span>
              </div>

              {/* Trust Indicators */}
              <div className="trust-indicators">
                <div className="trust-item">
                  <LockFill size={20} />
                  <h3>Central Bank Authorised</h3>
                  <p>Your bank connection is secure and regulated.</p>
                </div>
                <div className="trust-item">
                  <PersonLinesFill size={20} />
                  <h3>Irish Support</h3>
                  <p>Real help from Irish experts is just a click away.</p>
                </div>
                <div className="trust-item">
                  <ShieldCheck size={20} />
                  <h3>Data Protection</h3>
                  <p>Your data is safe, backed up, and belongs to you.</p>
                </div>
              </div>
            </div>
          </Form>
        </Container>
      </div>

      {/* Footer */}
      <div className="checkout-footer">
        <Container>
          <div className="footer-links">
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <a href="/support" target="_blank" rel="noopener noreferrer">
              Support
            </a>
            <span>&copy; 2026 Bullet</span>
          </div>
        </Container>
      </div>
    </div>
  );
};
